import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Link,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import { CopyClipboardInput } from 'features/common/components/CopyClipboardInput';
import { RegenerationErrorType } from 'features/context-ids/types/types';
import { errorMessages } from 'features/context-ids/utils/errorUtils';
import { useCallback, useEffect, useRef } from 'react';
import { useGetDeploymentInfo } from './hooks/useGetDeploymentInfo';
import { useRegenerateContextId } from './hooks/useRegenerateContextId';
import { EnvironmentDetails } from './models/environment.model';
import { RegenerateContextIdModalProps } from './models/regenerateContextIdModalProps';

const AlertBodyContent = ({
  deploymentInfo,
}: {
  deploymentInfo: EnvironmentDetails | undefined;
}): JSX.Element => (
  <>
    <Text>
      <T _str='Permanently revoke the current Context IDs and generate new ones? All apps and services using the current Context IDs will need to be updated with the new Context ID. You must also redeploy the XM Cloud environment. ' />
      <Link
        href='https://doc.sitecore.com/xmc/en/developers/xm-cloud/index-en.html?contextId=regenerate-context-ids'
        isExternal
      >
        Learn more
      </Link>
    </Text>
    <Text>
      <T _str='Before revoking the current Context IDs, copy and save them:' />
    </Text>
    <CopyClipboardInput
      inputValueProp={deploymentInfo?.previewContextId || ''}
      inputLabel={'Current Context ID (Preview)'}
      dataTestId='regenerate-contextid-modal-preview-contextid'
      isReadOnly
    />
    <CopyClipboardInput
      inputValueProp={deploymentInfo?.liveContextId || ''}
      inputLabel={'Current Context ID (Live)'}
      dataTestId='regenerate-contextid-modal-live-contextid'
      isReadOnly
    />
  </>
);

export const RegenerateContextIdModal = ({
  isOpen,
  onClose,
  contextId,
  applicationsNodes,
}: RegenerateContextIdModalProps) => {
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const { data: deploymentInfo, isLoading: isContextLoading } =
    useGetDeploymentInfo(applicationsNodes, contextId ?? '');

  const { mutateAsync: regenerateMutate, isLoading } =
    useRegenerateContextId(deploymentInfo);

  const onSubmit = useCallback(async () => {
    try {
      await regenerateMutate();
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  }, [regenerateMutate, onClose]);

  useEffect(() => {
    if (
      isOpen &&
      contextId &&
      (!applicationsNodes || applicationsNodes?.length === 0)
    ) {
      toast({
        status: 'warning',
        title: 'No Tenant Applications Found',
        description:
          errorMessages[RegenerationErrorType.NO_TENANT_APPLICATIONS],
        isClosable: true,
        duration: 5000,
      });
      onClose();
    }
  }, [isOpen, contextId, applicationsNodes, onClose, toast]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size='xl'
    >
      <AlertDialogOverlay>
        <AlertDialogContent data-testid='regenerate-contextid-modal'>
          <AlertDialogHeader>Regenerate Context IDs</AlertDialogHeader>
          <AlertDialogBody as={Stack} gap='5'>
            {isContextLoading || !deploymentInfo ? (
              <Flex justifyContent='center' alignItems='center' height='100px'>
                <Spinner />
              </Flex>
            ) : (
              <AlertBodyContent deploymentInfo={deploymentInfo} />
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Flex flexDirection='row' gap='2'>
              <Button
                onClick={onClose}
                data-testid='cancel-button'
                data-behavior-analytics-id='cancel-regenerate-contextid-modal-button'
                variant='ghost'
                ref={cancelRef}
              >
                Cancel
              </Button>
              <Button
                colorScheme='warning'
                onClick={onSubmit}
                isLoading={isLoading}
                type='submit'
                isDisabled={isContextLoading || !deploymentInfo}
                data-testid='regenerate-contextid-modal-submit-button'
                data-behavior-analytics-id='confirm-regenerate-contextid-modal-button'
              >
                Regenerate
              </Button>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
